import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BackButton } from '@/components/ui/back-button';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { toneClasses, type BoardTone } from './airport-tokens';
import { PHASE_TONE, type TripPhase } from './pnr-steps';
import { SERVICE_LOOK, type PnrService } from './service-look';

// The passenger card's small, shared pieces (PO 2026-10-31 redesign): the vocabulary
// chip, the service card box, the section title and a detail view's sub-header. Body
// text is 15 px; capitals only on section titles; colour by meaning (airport-tokens) —
// and each service by sight, its icon and hue (service-look.ts).

/** The card's one vocabulary as a chip: Listo · En curso · Necesita decisión · No aplica · … */
export function PhaseChip({
  phase,
  label,
  testId,
}: {
  phase: TripPhase;
  label?: string | undefined;
  testId?: string | undefined;
}) {
  const { t } = useTranslation('pnr');
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center rounded-md px-2 py-0.5 text-xs font-bold ring-1 ring-inset',
        toneClasses(PHASE_TONE[phase]).chip,
      )}
      data-phase={phase}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {label ?? t(`card.phase.${phase}`)}
    </span>
  );
}

/** A section title — the only capitals on the card. */
export function SectionTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h3 className={cn('text-xs font-bold uppercase tracking-[0.08em] text-muted-foreground', className)}>
      {children}
    </h3>
  );
}

/** A service's icon in its hue: a tile beside a card's title, or small inside a line. */
export function ServiceIcon({
  service,
  tile = false,
  className,
}: {
  service: PnrService;
  tile?: boolean;
  className?: string;
}) {
  const look = SERVICE_LOOK[service];
  const Icon = look.icon;
  if (!tile)
    return (
      <Icon
        aria-hidden
        className={cn('h-3.5 w-3.5 shrink-0', look.text, className)}
        data-service-icon={service}
      />
    );
  return (
    <span
      aria-hidden
      className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', look.tile, className)}
      data-service-icon={service}
    >
      <Icon className="h-5 w-5" />
    </span>
  );
}

/**
 * One service on the card: its title, where it stands, its detail and its own actions.
 * With a `service`, its icon tile beside the title and its hue down the left edge.
 */
export function ServiceCard({
  title,
  service,
  status,
  statusTone = 'idle',
  attention = false,
  actions,
  children,
  testId,
}: {
  title: string;
  /** Which service it is — drawn with its icon and hue (service-look.ts). */
  service?: PnrService | undefined;
  status?: ReactNode;
  statusTone?: BoardTone;
  /** Drawn with the warn border — the service needing the decision. */
  attention?: boolean;
  actions?: ReactNode;
  children?: ReactNode;
  testId: string;
}) {
  return (
    <section
      className={cn(
        'relative flex flex-col gap-2.5 overflow-hidden rounded-xl border bg-card p-4',
        service && 'pl-5',
        attention ? toneClasses('warn').border : 'border-border',
      )}
      aria-label={title}
      data-testid={testId}
      data-service={service}
    >
      {service ? (
        <span aria-hidden className={cn('absolute inset-y-0 left-0 w-1.5', SERVICE_LOOK[service].stripe)} />
      ) : null}
      <div
        className={cn(
          'flex flex-wrap justify-between gap-x-3 gap-y-1',
          service ? 'items-center' : 'items-baseline',
        )}
      >
        <h3 className="flex items-center gap-2.5 text-base font-bold">
          {service ? <ServiceIcon service={service} tile /> : null}
          {title}
        </h3>
        {status ? (
          <span className={cn('text-[13px]', toneClasses(statusTone).text)} data-testid={`${testId}-state`}>
            {status}
          </span>
        ) : null}
      </div>
      {children}
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </section>
  );
}

/** The outline action every service card uses (36 px, 44 px on touch via the workspace CSS). */
export function CardAction({
  children,
  onClick,
  disabled,
  title,
  testId,
  tone = 'default',
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  testId?: string;
  tone?: 'default' | 'danger';
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn(
        'h-9 px-3 text-[13px]',
        tone === 'danger' && 'border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive',
      )}
      onClick={onClick}
      disabled={disabled}
      {...(title ? { title } : {})}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {children}
    </Button>
  );
}

/**
 * A detail view's fixed head: what this view is, its actions, and the console's one way back
 * (`BackButton`: the round arrow at the top right, PO 2026-10-27/11-03) — no PNR, the card's
 * header above already names it.
 */
export function ViewHeader({
  title,
  onBack,
  actions,
}: {
  title: string;
  onBack: () => void;
  actions?: ReactNode;
}) {
  const { t } = useTranslation('pnr');
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border px-4 py-2 sm:px-6">
      <h2 className="min-w-0 flex-1 text-base font-semibold">{title}</h2>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      <BackButton label={t('card.back')} onClick={onBack} testId="pnr-view-back" />
    </div>
  );
}
